import { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowDown,
  faCamera,
  faCheck,
  faCheckCircle,
  faCheckDouble,
  faClose,
  faDownload,
  faEdit,
  faExclamationCircle,
  faExclamationTriangle,
  faImage,
  faInfoCircle,
  faPaperPlane,
  faQuestionCircle,
  faTrash,
  faUser,
  faBan,
} from '@fortawesome/free-solid-svg-icons';
import './ChatScreen.css';

const API_BASE_URL = 'https://mhsdev.online/MHS-WEB/api';

// --------------------------------------------
// Helpers
// --------------------------------------------
const formatTimestamp = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isWithinEditWindow = (createdAt) => {
  if (!createdAt) return false;
  return Date.now() - new Date(createdAt).getTime() < 24 * 60 * 60 * 1000;
};

const normalizeMobile = (mobile) => {
  if (!mobile) return '';
  return mobile.replace(/-/g, '');
};

// --------------------------------------------
// Enhanced Custom Modal (Supports Types)
// --------------------------------------------
const CustomModal = ({
  isOpen,
  onClose,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  showCancel = false,
  onConfirm,
  type = 'info',
}) => {
  if (!isOpen) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'success': return faCheckCircle;
      case 'error': return faExclamationCircle;
      case 'warning': return faExclamationTriangle;
      case 'confirm': return faQuestionCircle;
      default: return faInfoCircle;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'success': return '#27ae60';
      case 'error': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'confirm': return '#3498db';
      default: return '#3498db';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <div className="ch-custom-modal-overlay" onClick={onClose}>
      <div className="ch-custom-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ch-custom-modal-header">
          <div className="ch-modal-icon-title">
            <FontAwesomeIcon icon={getIcon(type)} style={{ color: getColor(type) }} size="lg" />
            <h3>{title}</h3>
          </div>
          <button className="ch-modal-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        <div className="ch-custom-modal-body">
          <p>{message}</p>
        </div>
        <div className="ch-custom-modal-footer">
          {showCancel && (
            <button className="ch-modal-btn ch-cancel-btn" onClick={onClose}>
              {cancelText}
            </button>
          )}
          <button 
            className="ch-modal-btn ch-confirm-btn" 
            onClick={handleConfirm} 
            style={{ backgroundColor: getColor(type) }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------
// MessageBubble Component
// --------------------------------------------
const MessageBubble = ({
  message,
  timestamp,
  isOutgoing,
  isRead,
  message_type,
  is_edited,
  isLast,
  onLongPress,
  onPressImage,
  onImageLoad,
}) => {
  const imageUri = message.startsWith('http')
    ? message
    : `${API_BASE_URL}/${message}`;

  const handleClick = () => {
    if (message_type === 'image') {
      onPressImage?.(imageUri);
    }
  };

  return (
    <div className={`ch-bubble-wrapper ${isOutgoing ? 'ch-bubble-out' : 'ch-bubble-in'}`}>
      <div
        className={`ch-bubble ${isOutgoing ? 'ch-bubble-outgoing' : 'ch-bubble-incoming'}`}
        onClick={handleClick}
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPress?.();
        }}
      >
        {message_type === 'image' ? (
          <img src={imageUri} alt="message" className="ch-bubble-image" onLoad={() => { if (isLast) onImageLoad?.(); }} />
        ) : (
          <p className="ch-bubble-text">{message || ''}</p>
        )}

        <div className="ch-bubble-footer">
          <span className="ch-bubble-timestamp">
            {timestamp}
            {is_edited && ' • edited'}
          </span>
          {isOutgoing && (
            <FontAwesomeIcon
              icon={isRead ? faCheckDouble : faCheck}
              size="xs"
              style={{ color: isRead ? '#53bdeb' : '#888', marginLeft: 4 }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------
// Main Component
// --------------------------------------------
const ChatScreen = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { otherUserName = 'User', otherUserMobile, otherUserType = 'parent' } = location.state || {};

  // --- State ---
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  const [otherUserAvatar, setOtherUserAvatar] = useState(null);
  const [otherUserNickname, setOtherUserNickname] = useState('');

  const [modalImageUri, setModalImageUri] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // --- Scroll control ---
  // isAtBottom (state) drives the floating button's visibility.
  // isAtBottomRef (ref) is read inside the poll interval / effects, since those
  // closures would otherwise see a stale value of isAtBottom frozen from
  // whenever they were first created — this was the actual cause of "can't
  // scroll up", because every incoming message force-scrolled regardless of
  // where the user currently was.
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const messagesAreaRef = useRef(null);

  const updateIsAtBottom = (value) => {
    isAtBottomRef.current = value;
    setIsAtBottom(value);
  };

  // --- Profile Modal State ---
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [profileData, setProfileData] = useState({
    nickname: null,
    bio: null,
    avatar_url: null,
  });
  const [loadingProfile, setLoadingProfile] = useState(false);

  // --- Session Status State ---
  const [sessionStatus, setSessionStatus] = useState(null);
  const [loadingSessionStatus, setLoadingSessionStatus] = useState(false);

  // --- Expire Session Modal State ---
  const [expireModalVisible, setExpireModalVisible] = useState(false);
  const [cnicInput, setCnicInput] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const lockoutIntervalRef = useRef(null);

  // --- Custom Modal State ---
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false,
    onConfirm: null,
    type: 'info',
  });

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const hasScrolledOnOpenRef = useRef(false);

  // --- Custom Modal Helpers ---
  const showAlert = (title, message, type = 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      confirmText: 'OK',
      showCancel: false,
      onConfirm: null,
      type,
    });
  };

  const showConfirm = (title, message, onConfirm) => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm,
      type: 'confirm',
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  // --- Load Current User ---
  useEffect(() => {
    const storedId = localStorage.getItem('userId');
    const storedType = localStorage.getItem('userType');
    const storedMobile = localStorage.getItem('userMobile');

    if (storedId && storedType && storedMobile) {
      setCurrentUser({
        id: storedId,
        type: storedType,
        mobile: normalizeMobile(storedMobile),
      });
    } else {
      showAlert('Session Error', 'User data not found. Please log in again.', 'error');
      navigate('/auth/phone');
    }
    setLoadingUser(false);
  }, [navigate]);

  // --- Scroll to Bottom ---
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    updateIsAtBottom(true);
  };

  // --- Detect user's scroll position within the messages panel ---
  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const threshold = 60; // px tolerance from the true bottom
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    updateIsAtBottom(atBottom);
  };

  // Only auto-scroll when new messages arrive IF the user was already at the
  // bottom. This is what actually restores free scrolling — previously this
  // effect scrolled unconditionally on every messages change.
  useEffect(() => {
    if (messages.length === 0) return;
    if (isAtBottomRef.current) {
      scrollToBottom(hasScrolledOnOpenRef.current ? 'smooth' : 'auto');
    }
    hasScrolledOnOpenRef.current = true;
  }, [messages]);

  // --- Fetch Conversation History ---
  const fetchConversationHistory = async () => {
    if (!otherUserMobile || !currentUser?.mobile) return;

    try {
      setIsLoading(true);
      const normalizedSender = normalizeMobile(currentUser.mobile);
      const normalizedReceiver = normalizeMobile(otherUserMobile);

      const res = await axios.get(`${API_BASE_URL}/messages_api.php`, {
        params: {
          action: 'get_conversation',
          sender_mobile: normalizedSender,
          receiver_mobile: normalizedReceiver,
        },
      });

      if (res.data && Array.isArray(res.data)) {
        const formatted = res.data.map((item) => ({
          id: String(item.message_id || Math.random()),
          message: item.message_body || '',
          timestamp: formatTimestamp(item.created_at),
          isOutgoing: normalizeMobile(item.sender_mobile) === normalizedSender,
          isRead: parseInt(item.is_read) === 1,
          message_type: item.message_type || 'text',
          is_edited: parseInt(item.is_edited) === 1,
          created_at: item.created_at,
        }));

        setMessages(formatted);
        if (formatted.length > 0) {
          const maxId = Math.max(...res.data.map((i) => parseInt(i.message_id) || 0));
          lastMessageIdRef.current = maxId;
        }
      }
    } catch (e) {
      console.error('Error fetching chat history:', e);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Poll for New Messages ---
  const pollForNewMessages = async () => {
    if (!otherUserMobile || !currentUser?.mobile) return;

    try {
      const normalizedSender = normalizeMobile(currentUser.mobile);
      const normalizedReceiver = normalizeMobile(otherUserMobile);

      const res = await axios.get(`${API_BASE_URL}/messages_api.php`, {
        params: {
          action: 'poll_messages',
          sender_mobile: normalizedSender,
          receiver_mobile: normalizedReceiver,
          last_message_id: lastMessageIdRef.current,
        },
      });

      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        // IMPORTANT: dedupe against the functional-updater's `prev`, the LIVE
        // current message list — not any outer-scope variable. This is what
        // stops a message from appearing twice: without this check, if the
        // backend's "last_message_id" comparison is inclusive (or this poll
        // races with the one triggered right after sending), the same
        // message could get appended a second time with the same id.
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));

          const incoming = res.data
            .map((item) => ({
              id: String(item.message_id || Math.random()),
              message: item.message_body || '',
              timestamp: formatTimestamp(item.created_at),
              isOutgoing: normalizeMobile(item.sender_mobile) === normalizedSender,
              isRead: parseInt(item.is_read) === 1,
              message_type: item.message_type || 'text',
              is_edited: parseInt(item.is_edited) === 1,
              created_at: item.created_at,
            }))
            .filter((m) => !existingIds.has(m.id));

          // Always advance the high-water mark from the raw response, even
          // for entries we filtered out as duplicates — otherwise we'd keep
          // re-requesting the same already-seen id forever.
          const maxId = Math.max(...res.data.map((i) => parseInt(i.message_id) || 0));
          lastMessageIdRef.current = Math.max(lastMessageIdRef.current, maxId);

          if (incoming.length === 0) return prev;
          return [...prev, ...incoming];
        });
      }
    } catch (e) {
      // Silent fail
    }
  };

  // --- Fetch Other User Profile (for header) ---
  const fetchOtherUserProfile = async () => {
    if (!otherUserMobile) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/mhs_wa_profile_api.php`, {
        params: { action: 'get_profile', mobile: normalizeMobile(otherUserMobile) },
      });
      if (res.data) {
        setOtherUserAvatar(res.data.avatar_url || null);
        setOtherUserNickname(res.data.nickname || '');
      }
    } catch (e) {
      console.error('Error fetching other user profile:', e);
    }
  };

  // --- Fetch Full Profile for Modal ---
  const fetchProfileForModal = async () => {
    if (!otherUserMobile) return;
    setLoadingProfile(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/mhs_wa_profile_api.php`, {
        params: { action: 'get_profile', mobile: normalizeMobile(otherUserMobile) },
        timeout: 10000,
      });
      const data = res.data;
      setProfileData({
        nickname: data.nickname || null,
        bio: data.bio || null,
        avatar_url: data.avatar_url || null,
      });
    } catch (e) {
      console.error('Error fetching profile for modal:', e);
      showAlert('Error', 'Could not load profile information.', 'error');
    } finally {
      setLoadingProfile(false);
    }
  };

  // --- Fetch Session Status (for expire button) ---
  const fetchSessionStatus = async () => {
    if (!otherUserMobile) return;
    setLoadingSessionStatus(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/std_auth_api.php`, {
        params: {
          action: 'check_session_status',
          mobile: normalizeMobile(otherUserMobile),
        },
        timeout: 5000,
      });
      if (res.data && res.data.status) {
        setSessionStatus(res.data.status);
      } else {
        setSessionStatus('none');
      }
    } catch (e) {
      console.error('Error fetching session status:', e);
      setSessionStatus('none');
    } finally {
      setLoadingSessionStatus(false);
    }
  };

  // --- Open Profile Modal ---
  const openProfileModal = () => {
    fetchProfileForModal();
    fetchSessionStatus();
    setProfileModalVisible(true);
  };

  // --- Send Text Message ---
  const sendMessage = async () => {
    if (isSending || !currentUser) return;

    if (selectedImage) {
      await sendMediaMessage(selectedImage, 'image');
      setSelectedImage(null);
      return;
    }

    if (!inputText.trim()) return;

    const payload = {
      sender_id: currentUser.id,
      sender_type: currentUser.type,
      sender_mobile: normalizeMobile(currentUser.mobile),
      receiver_id: id,
      receiver_type: otherUserType || 'parent',
      receiver_mobile: normalizeMobile(otherUserMobile),
      message_body: inputText.trim(),
      message_type: 'text',
    };

    setIsSending(true);
    const currentText = inputText.trim();
    setInputText('');

    const tempId = Date.now().toString();
    const tempMessage = {
      id: tempId,
      message: currentText,
      timestamp: formatTimestamp(new Date().toISOString()),
      isOutgoing: true,
      isRead: false,
      message_type: 'text',
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMessage]);
    // Sending should always snap you to the bottom, like WhatsApp — even if
    // you'd scrolled up to read older messages beforehand.
    scrollToBottom('smooth');

    try {
      const res = await axios.post(
        `${API_BASE_URL}/messages_api.php?action=send_message`,
        payload,
        { timeout: 15000 }
      );

      if (res.data.success) {
        const serverId = String(res.data.message_id);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempId ? { ...msg, id: serverId } : msg))
        );
        lastMessageIdRef.current = Math.max(lastMessageIdRef.current, parseInt(serverId) || 0);
        // Safe to call even redundantly now — pollForNewMessages dedupes by id.
        pollForNewMessages();
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
        setInputText(currentText);
        showAlert('Error', 'Failed to send message.', 'error');
      }
    } catch (e) {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setInputText(currentText);
      showAlert('Error', 'Could not send message. Check your connection.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  // --- Send Media Message ---
  const sendMediaMessage = async (mediaUri, type) => {
    if (isSending || !currentUser) return;

    setIsSending(true);
    const tempId = Date.now().toString();

    let fileBlob;
    try {
      const res = await fetch(mediaUri);
      fileBlob = await res.blob();
    } catch (e) {
      console.error(e);
      showAlert('Error', 'Could not process image.', 'error');
      setIsSending(false);
      return;
    }

    const formData = new FormData();
    formData.append('sender_id', currentUser.id);
    formData.append('sender_type', currentUser.type);
    formData.append('sender_mobile', normalizeMobile(currentUser.mobile));
    formData.append('receiver_id', id);
    formData.append('receiver_type', otherUserType || 'parent');
    formData.append('receiver_mobile', normalizeMobile(otherUserMobile));
    formData.append('message_type', type);
    formData.append('message_body', '');
    formData.append('media', fileBlob, `${Date.now()}.jpg`);

    const tempMessage = {
      id: tempId,
      message: mediaUri,
      timestamp: formatTimestamp(new Date().toISOString()),
      isOutgoing: true,
      isRead: false,
      message_type: type,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMessage]);
    scrollToBottom('smooth');

    try {
      const res = await axios.post(
        `${API_BASE_URL}/messages_api.php?action=send_message`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
      );

      if (res.data.success) {
        const serverId = String(res.data.message_id);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempId ? { ...msg, id: serverId } : msg))
        );
        lastMessageIdRef.current = Math.max(lastMessageIdRef.current, parseInt(serverId) || 0);
        pollForNewMessages();
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
        showAlert('Error', 'Failed to send image.', 'error');
      }
    } catch (e) {
      console.error('Upload error:', e);
      showAlert('Error', 'Could not upload image.', 'error');
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
    } finally {
      setIsSending(false);
    }
  };

  // --- Image Pickers (Web) ---
  const pickImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedImage(URL.createObjectURL(file));
      }
    };
    input.click();
  };

  const takePhoto = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedImage(URL.createObjectURL(file));
      }
    };
    input.click();
  };

  // --- Download Image ---
  const downloadImage = async (uri) => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const fileName = uri.split('/').pop();
      if (!fileName) throw new Error('Invalid URI');
      const downloadUrl = `${API_BASE_URL}/download.php?file=${encodeURIComponent(fileName)}`;

      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();

      if (window.navigator && window.navigator.msSaveBlob) {
        window.navigator.msSaveBlob(blob, fileName);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      showAlert('Success', 'Image downloaded.', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Error', 'Could not download the image.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Delete Message ---
  const deleteMessage = async (messageId, senderMobile) => {
    try {
      const payload = {
        message_id: String(messageId),
        sender_mobile: normalizeMobile(senderMobile),
      };
      const res = await axios.post(
        `${API_BASE_URL}/messages_api.php?action=delete_message`,
        payload
      );
      if (res.data.success) {
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      } else {
        showAlert('Delete Failed', res.data.error || 'Unknown error', 'error');
      }
    } catch (e) {
      console.error('Delete error:', e);
      showAlert('Network Error', 'Could not delete message.', 'error');
    }
  };

  // --- Edit Message ---
  const editMessage = async (messageId, newText, senderMobile) => {
    try {
      setIsSavingEdit(true);
      const res = await axios.post(
        `${API_BASE_URL}/messages_api.php?action=edit_message`,
        {
          message_id: messageId,
          new_message_body: newText,
          sender_mobile: normalizeMobile(senderMobile),
        }
      );
      if (res.data.success) {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, message: newText, is_edited: true } : msg))
        );
        setIsEditing(false);
      } else {
        showAlert('Edit Failed', res.data.error || 'Could not edit message.', 'error');
      }
    } catch (e) {
      showAlert('Network Error', 'Could not edit message.', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // --- Long Press Handler ---
  const handleTextLongPress = (msg) => {
    setActionMessage(msg);
    setActionModalVisible(true);
  };

  // --- Expire Session Handlers ---
  const handleExpirePress = () => {
    if (currentUser?.type !== 'admin') {
      showAlert('Access Denied', 'Only administrators can expire a user session.', 'warning');
      return;
    }

    if (sessionStatus === 'expired') {
      showAlert('Already Expired', 'This user’s session has already been expired.', 'info');
      return;
    }

    if (sessionStatus === 'none') {
      showAlert('No Session Found', 'This user has never logged in, so there is no session to expire.', 'info');
      return;
    }

    setCnicInput('');
    setErrorMessage(null);
    setFailedAttempts(0);
    setIsLockedOut(false);
    if (lockoutIntervalRef.current) {
      clearInterval(lockoutIntervalRef.current);
      lockoutIntervalRef.current = null;
    }
    setLockoutTimeLeft(0);
    setExpireModalVisible(true);
  };

  const handleCloseExpireModal = () => {
    setExpireModalVisible(false);
    if (lockoutIntervalRef.current) {
      clearInterval(lockoutIntervalRef.current);
      lockoutIntervalRef.current = null;
    }
  };

  // --- Actual Expiry Request (NO EXTRA FRONTEND CHECKS, NEW ENDPOINT) ---
  const handleVerifyCnic = async () => {
    if (isLockedOut || isVerifying) return;
    if (!cnicInput || cnicInput.length !== 5) {
      setErrorMessage('Please enter exactly 5 digits.');
      return;
    }

    setErrorMessage(null);
    setIsVerifying(true);

    if (!currentUser || !currentUser.id) {
      setErrorMessage('Admin user ID missing.');
      setIsVerifying(false);
      return;
    }
    if (!otherUserMobile) {
      setErrorMessage('Target user mobile number is missing.');
      setIsVerifying(false);
      return;
    }

    // ⚠️ CHANGE THIS TO MATCH YOUR PHP $WEB_ADMIN_SECRET
    const webAdminSecret = 'WEB_SECRET_KEY_CHANGE_THIS_TO_A_LONG_RANDOM_STRING';

    const payload = {
      web_admin_secret: webAdminSecret,
      target_mobile: normalizeMobile(otherUserMobile),
      target_cnic_last5: cnicInput,
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/std_auth_api.php?action=expire_session_web`,
        payload
      );

      if (response.data.success) {
        setExpireModalVisible(false);
        setFailedAttempts(0);
        setCnicInput('');
        showAlert('Success', 'The parent session has been successfully expired.', 'success');
        fetchSessionStatus();
      } else {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);

        if (newAttempts >= 3) {
          setIsLockedOut(true);
          setLockoutTimeLeft(120);
          setErrorMessage('Too many failed attempts. Please wait 2 minutes.');
          lockoutIntervalRef.current = setInterval(() => {
            setLockoutTimeLeft((prev) => {
              if (prev <= 1) {
                clearInterval(lockoutIntervalRef.current);
                lockoutIntervalRef.current = null;
                setIsLockedOut(false);
                setFailedAttempts(0);
                setErrorMessage(null);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          setErrorMessage(`Incorrect CNIC. ${3 - newAttempts} attempt(s) remaining.`);
        }
      }
    } catch (error) {
      console.error('Expire Session failed:', error);
      const msg = error.response?.data?.error || 'Network error. Please try again.';
      setErrorMessage(msg);
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Main Effects ---
  useEffect(() => {
    if (!currentUser) return;
    // Always start a freshly-opened conversation pinned to bottom, even if
    // the user had scrolled up while viewing a different conversation.
    updateIsAtBottom(true);
    hasScrolledOnOpenRef.current = false;

    fetchConversationHistory();
    fetchOtherUserProfile();

    pollIntervalRef.current = setInterval(pollForNewMessages, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (lockoutIntervalRef.current) {
        clearInterval(lockoutIntervalRef.current);
      }
    };
  }, [otherUserMobile, currentUser]);

  // --------------------------------------------
  // Render
  // --------------------------------------------
  if (loadingUser) {
    return (
      <div className="ch-loading-screen">
        <div className="ch-spinner"></div>
        <p>Loading session...</p>
      </div>
    );
  }

  return (
    <div className="ch-chat-container">
      {/* Header */}
      <div className="ch-header">
        <button className="ch-back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="ch-user-info" onClick={openProfileModal} style={{ cursor: 'pointer' }}>
          {otherUserAvatar ? (
            <img src={otherUserAvatar} alt="avatar" className="ch-avatar-small" />
          ) : (
            <div className="ch-avatar-placeholder">
              {(otherUserName || otherUserNickname || 'A').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="ch-user-details">
            <span className="ch-user-name">{otherUserName || otherUserNickname || 'Chat'}</span>
            <span className="ch-user-status">{otherUserType ? String(otherUserType) : 'online'}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="ch-messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
        {isLoading && messages.length === 0 ? (
          <div className="ch-loading-messages">
            <div className="ch-spinner"></div>
          </div>
        ) : (
          <div className="ch-messages-list">
            {messages.map((msg, index) => (
              <MessageBubble
                key={msg.id}
                {...msg}
                isLast={index === messages.length - 1}
                onImageLoad={() => {
                  // A late-loading image can grow the content taller than
                  // when we first scrolled — re-pin to bottom, but only if
                  // the user is still supposed to be there.
                  if (isAtBottomRef.current) scrollToBottom('auto');
                }}
                onLongPress={() => {
                  if (msg.message_type === 'text') {
                    handleTextLongPress(msg);
                  } else if (msg.message_type === 'image') {
                    const uri = msg.message.startsWith('http') ? msg.message : `${API_BASE_URL}/${msg.message}`;
                    setModalImageUri(uri);
                    setActionMessage(msg);
                    setModalVisible(true);
                  }
                }}
                onPressImage={(uri) => {
                  setModalImageUri(uri);
                  setActionMessage(msg);
                  setModalVisible(true);
                }}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Floating "jump to latest" button — appears only once the user has
            scrolled away from the bottom. Styled inline so it works even if
            ChatScreen.css hasn't been updated yet; feel free to move these
            styles into a .ch-scroll-bottom-btn class in the stylesheet. */}
        {!isAtBottom && messages.length > 0 && (
          <button
            className="ch-scroll-bottom-btn"
            onClick={() => scrollToBottom('smooth')}
            style={{
              position: 'absolute',
              right: 20,
              bottom: 90,
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#128C7E',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              zIndex: 10,
            }}
            aria-label="Scroll to latest message"
          >
            <FontAwesomeIcon icon={faArrowDown} />
          </button>
        )}
      </div>

      {/* Input Area */}
      <div className="ch-input-area">
        {selectedImage ? (
          <div className="ch-image-preview">
            <img src={selectedImage} alt="preview" className="ch-preview-img" />
            <button className="ch-remove-image-btn" onClick={() => setSelectedImage(null)}>
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
        ) : (
          <div className="ch-input-pill">
            <button className="ch-input-icon" onClick={pickImage}>
              <FontAwesomeIcon icon={faImage} />
            </button>
            <button className="ch-input-icon" onClick={takePhoto}>
              <FontAwesomeIcon icon={faCamera} />
            </button>
            <input
              className="ch-text-input"
              type="text"
              placeholder="Message"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={isSending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
          </div>
        )}
        <button
          className={`ch-send-btn ${inputText.trim() || selectedImage ? 'active' : ''}`}
          onClick={sendMessage}
          disabled={(!inputText.trim() && !selectedImage) || isSending || !currentUser}
        >
          {isSending ? <div className="ch-spinner-small" /> : <FontAwesomeIcon icon={faPaperPlane} />}
        </button>
      </div>

      {/* Image Modal */}
      {modalVisible && (
        <div className="ch-modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="ch-modal-content" onClick={(e) => e.stopPropagation()}>
            {modalImageUri && <img src={modalImageUri} alt="full" className="ch-modal-image" />}
            <button className="ch-modal-close" onClick={() => setModalVisible(false)}>
              <FontAwesomeIcon icon={faClose} />
            </button>
            <div className="ch-modal-actions">
              <button onClick={() => modalImageUri && downloadImage(modalImageUri)} disabled={isSaving}>
                {isSaving ? <div className="ch-spinner-small" /> : <FontAwesomeIcon icon={faDownload} />}
                <span>Download</span>
              </button>
              <button
                className="ch-modal-delete"
                onClick={() => {
                  if (actionMessage && isWithinEditWindow(actionMessage.created_at) && currentUser) {
                    showConfirm('Delete image', 'Are you sure you want to delete this image?', () => {
                      deleteMessage(actionMessage.id, currentUser.mobile);
                      setModalVisible(false);
                    });
                  } else {
                    showAlert('Cannot Delete', 'This image is older than 24 hours and cannot be deleted.', 'warning');
                  }
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModalVisible && (
        <div className="ch-modal-overlay" onClick={() => setActionModalVisible(false)}>
          <div className="ch-action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ch-action-header">
              <h4>Message Actions</h4>
              <button onClick={() => setActionModalVisible(false)}>
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            {actionMessage && isWithinEditWindow(actionMessage.created_at) ? (
              <div className="ch-action-buttons">
                {actionMessage.message_type === 'text' && (
                  <button
                    className="ch-action-btn"
                    onClick={() => {
                      setActionModalVisible(false);
                      setEditText(actionMessage.message);
                      setIsEditing(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    <span>Edit</span>
                  </button>
                )}
                <button
                  className="ch-action-btn delete"
                  onClick={() => {
                    setActionModalVisible(false);
                    showConfirm('Delete message', 'Are you sure you want to delete this message?', () => {
                      if (currentUser) deleteMessage(actionMessage.id, currentUser.mobile);
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span>Delete</span>
                </button>
              </div>
            ) : (
              <p className="ch-action-info">Message is older than 24 hours – cannot edit or delete.</p>
            )}
            <button className="ch-action-btn cancel" onClick={() => setActionModalVisible(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      {isEditing && (
        <div className="ch-modal-overlay" onClick={() => setIsEditing(false)}>
          <div className="ch-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ch-edit-header">
              <h4>Edit Message</h4>
              <button onClick={() => setIsEditing(false)}>
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            <div className="ch-edit-body">
              <textarea
                className="ch-edit-textarea"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Enter new message"
                autoFocus
                rows={4}
              />
            </div>
            <div className="ch-edit-footer">
              <button className="ch-edit-cancel" onClick={() => setIsEditing(false)} disabled={isSavingEdit}>
                Cancel
              </button>
              <button
                className="ch-edit-save"
                onClick={() => {
                  if (editText.trim() && actionMessage && currentUser) {
                    editMessage(actionMessage.id, editText.trim(), currentUser.mobile);
                  } else {
                    showAlert('Error', 'Message cannot be empty.', 'error');
                  }
                }}
                disabled={isSavingEdit}
              >
                {isSavingEdit ? <div className="ch-spinner-small" /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModalVisible && (
        <div className="ch-modal-overlay" onClick={() => setProfileModalVisible(false)}>
          <div className="ch-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ch-profile-header">
              <h3>Profile</h3>
              <button className="ch-modal-close-btn" onClick={() => setProfileModalVisible(false)}>
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            {loadingProfile ? (
              <div className="ch-loading-profile">
                <div className="ch-spinner"></div>
              </div>
            ) : (
              <div className="ch-profile-content">
                <div className="ch-profile-avatar">
                  {profileData.avatar_url ? (
                    <img src={profileData.avatar_url} alt="avatar" className="ch-profile-avatar-img" />
                  ) : (
                    <div className="ch-profile-avatar-fallback">
                      {(profileData.nickname || otherUserName || 'A').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="ch-profile-field">
                  <span className="ch-profile-label">Name</span>
                  <span className="ch-profile-value">{profileData.nickname || otherUserName}</span>
                </div>
                <div className="ch-profile-field">
                  <span className="ch-profile-label">About</span>
                  <span className="ch-profile-value">{profileData.bio || 'No bio available'}</span>
                </div>
                <div className="ch-profile-field">
                  <span className="ch-profile-label">Mobile</span>
                  <span className="ch-profile-value">{otherUserMobile}</span>
                </div>
                {currentUser?.type === 'admin' && (
                  <button
                    className={`ch-profile-expire-btn ${
                      sessionStatus === 'active'
                        ? 'ch-profile-expire-btn-active'
                        : sessionStatus === 'expired'
                        ? 'ch-profile-expire-btn-expired'
                        : 'ch-profile-expire-btn-none'
                    }`}
                    onClick={handleExpirePress}
                    disabled={sessionStatus === 'none' || loadingSessionStatus}
                    title={
                      loadingSessionStatus
                        ? 'Checking session status...'
                        : sessionStatus === 'active'
                        ? 'Click to expire the active session'
                        : sessionStatus === 'expired'
                        ? 'Session already expired'
                        : 'No session record found'
                    }
                  >
                    {loadingSessionStatus ? (
                      <div className="ch-spinner-small" />
                    ) : (
                      <FontAwesomeIcon icon={faBan} style={{ marginRight: 8 }} />
                    )}
                    <span>{loadingSessionStatus ? 'Checking...' : 'Expire Parent Session'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expire CNIC Modal */}
      {expireModalVisible && (
        <div className="ch-modal-overlay" onClick={handleCloseExpireModal}>
          <div className="ch-expire-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ch-expire-header">
              <h4>Expire Session</h4>
              <button className="ch-modal-close-btn" onClick={handleCloseExpireModal}>
                <FontAwesomeIcon icon={faClose} />
              </button>
            </div>
            <div className="ch-expire-body">
              <div className="ch-expire-icon">
                <FontAwesomeIcon icon={faBan} size="3x" color="#d32f2f" />
              </div>
              <p className="ch-expire-subtitle">Enter your (admin) last 5 digits of CNIC to confirm.</p>
              <div className="ch-expire-input-wrap">
                <input
                  type="text"
                  className="ch-expire-input"
                  placeholder="Last 5 digits"
                  maxLength={5}
                  value={cnicInput}
                  onChange={(e) => {
                    setCnicInput(e.target.value.replace(/\D/g, ''));
                    setErrorMessage(null);
                  }}
                  disabled={isLockedOut || isVerifying}
                  autoFocus
                />
              </div>
              {errorMessage && <p className="ch-expire-error">{errorMessage}</p>}
              {isLockedOut && <p className="ch-expire-lockout">Please wait {lockoutTimeLeft}s before trying again.</p>}
              <div className="ch-expire-actions">
                <button className="ch-expire-cancel" onClick={handleCloseExpireModal} disabled={isVerifying}>
                  Cancel
                </button>
                <button className="ch-expire-confirm" onClick={handleVerifyCnic} disabled={isLockedOut || isVerifying}>
                  {isVerifying ? 'Verifying...' : 'Expire'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Custom Alert/Confirm Modal */}
      <CustomModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        showCancel={modalConfig.showCancel}
        onConfirm={modalConfig.onConfirm}
        type={modalConfig.type}
      />
    </div>
  );
};

export default ChatScreen;